/**
 * WorkflowPanelErrorBoundary
 * Error boundary for the workflow panel. A panel crash must NOT take down AppShell
 * (phase-web-ui.md). Rather than vanishing silently, it renders a small, self-contained
 * fallback so the user knows the panel — not the whole app — failed.
 */

import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class WorkflowPanelErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override componentDidCatch(error: Error) {
    // Log but do not surface to the user
    console.error('[WorkflowPanel] Panel error caught by boundary:', error);
  }

  override render() {
    if (this.state.hasError) {
      // Minimal, self-contained fallback (no app state, no hooks — cannot itself throw). Keeps the
      // failure scoped to the panel while telling the user it's the panel, not the whole app.
      return (
        <div
          data-testid="workflow-panel-error"
          role="note"
          className="m-4 p-3 rounded-lg border border-border bg-muted/30 text-xs text-muted-foreground"
        >
          Workflows are temporarily unavailable. Refresh the page to try again.
        </div>
      );
    }
    return this.props.children;
  }
}
